/**
 * ESP32DownloadCode.js – FULL SOURCE
 * React component for generating and downloading an ESP32 sketch.
 * Features:
 *  • Wi‑Fi configuration
 *  • Pin mapping (GPIO / Direction / Read mode)
 *  • Publishes sensor data or output state every 8 s via MQTT
 *  • Close button (×)
 */

import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import React, { useState, useEffect, useCallback } from "react";

/** @typedef {{ name: string, type?: string }} Control */

/**
 * @param {{
 *   deviceSerial?: string,
 *   selectedDevice?: { controls?: Control[] },
 *   onClose?: () => void
 * }} props
 */
export default function ESP32DownloadCode({isDarkMode,
  deviceSerial = "12345",
  selectedDevice,
  onClose = () => {}
}) {
  /* ────────────── state ────────────── */
  const bgColorClass = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const textColorClass = isDarkMode ? 'text-gray-100' : 'text-gray-800';


  const [ssid, setSsid] = useState("YOUR_WIFI_SSID");
  const [password, setPassword] = useState("YOUR_WIFI_PASSWORD");
  const [pinMap, setPinMap] = useState({});
  const [sketch, setSketch] = useState("// Completa los campos…");

  /* ───────────── constants ─────────── */
  const MQTT_CRED_URL = "https://holmio.net/app/api_mqtt.php";
  const PUB_INTERVAL = 8000; // ms

  /* sync pinMap with controls */
  useEffect(() => {
    if (!selectedDevice?.controls?.length) {
      setPinMap({});
      return;
    }
    setPinMap((prev) => {
      const next = { ...prev };
      selectedDevice.controls.forEach((c) => {
        if (!next[c.name])
          next[c.name] = { pin: "", direction: "OUTPUT", read: "DIGITAL" };
      });
      Object.keys(next).forEach((k) => {
        if (!selectedDevice.controls.some((c) => c.name === k)) delete next[k];
      });
      return next;
    });
  }, [selectedDevice]);

  /* helper to update field */
  const setField = (ctrl, key, val) =>
    setPinMap((m) => ({ ...m, [ctrl]: { ...m[ctrl], [key]: val } }));

  /* ───────── generate sketch ───────── */
  const generateSketch = useCallback(() => {
    if (!selectedDevice?.controls?.length) {
      setSketch("// No hay controles.");
      return;
    }

    let pinDefs = "";
    let setupLines = "";
    let cbBody = "";
    let loopLines = "";

    selectedDevice.controls.forEach((ctrl) => {
      const cfg = pinMap[ctrl.name] || {
        pin: "",
        direction: "OUTPUT",
        read: "DIGITAL"
      };
      if (!cfg.pin || isNaN(Number(cfg.pin))) return;

      const K = ctrl.name.toUpperCase().replace(/[^A-Z0-9_]/g, "_");
       const D = ctrl.description || ctrl.name; // Descripción para MQTT
      pinDefs += `#define PIN_${K} ${cfg.pin}\n`;
      setupLines += `  pinMode(PIN_${K}, ${cfg.direction});\n`;

      if (cfg.direction === "OUTPUT") {
        setupLines += `  digitalWrite(PIN_${K}, LOW);\n`;
        cbBody += `
  if (String(topic) == topicBase + "/${D}/control") {
    String msg; for (unsigned int i=0;i<length;++i) msg += (char)payload[i];
    digitalWrite(PIN_${K}, msg=="ON" ? HIGH : LOW);
    mqtt.publish((topicBase + "/${D}/state").c_str(), msg.c_str());
    return;
  }`;
        loopLines += `
  static unsigned long last${K}=0;
  if (millis()-last${K}>PUB_INTERVAL){
    last${K}=millis();
    mqtt.publish((topicBase + "/${D}/state").c_str(),
                 digitalRead(PIN_${K})? "ON":"OFF");
  }`;
        return;
      }

      const readSnip =
        cfg.read === "ANALOG"
          ? `int val=analogRead(PIN_${K});
          char buf[8];
          itoa(val,buf,10);
          mqtt.publish((topicBase+"/${D}/data").c_str(),buf);`
          : `bool val = digitalRead(PIN_${K}); 
          mqtt.publish((topicBase+"/${D}/data").c_str(), val ? "HIGH" : "LOW");`;

      loopLines += `
  static unsigned long last${K}=0;
  if (millis()-last${K}>PUB_INTERVAL){
    last${K}=millis();
    ${readSnip}
  }`;
    });

    const ino = `/* Autogenerated by Holmio dashboard */
#include <WiFi.h>
#include <HTTPClient.h>
#include <WiFiClientSecure.h>
#include <PubSubClient.h>
#include <ArduinoJson.h>
#include "mbedtls/base64.h"
#include <time.h>

const unsigned long PUB_INTERVAL=${PUB_INTERVAL};

const char* WIFI_SSID="${ssid}";
const char* WIFI_PASS="${password}";
const char* CRED_URL="${MQTT_CRED_URL}";

String serial="${deviceSerial}";
String topicBase="home/"+serial;
String clientId;

${pinDefs}

WiFiClientSecure net;
PubSubClient mqtt(net);
String mqttUser,mqttPass,mqttHost;
uint16_t mqttPort=8883;

void connectWiFi();
bool downloadCredentials();
void mqttCallback(char* t, byte* p, unsigned int l);
void connectMQTT();
void initTime();


void setup(){
  Serial.begin(115200);
  clientId="ESP32_"+serial+"_"+String(random(1000,9999));
  connectWiFi();
  initTime();  // <- sincroniza reloj
  if(!downloadCredentials()){delay(5000); esp_restart();}
  connectMQTT();
${setupLines}}

void loop(){
  if(!mqtt.connected()) connectMQTT();
  mqtt.loop();
  static unsigned long lastStatusSent = 0;
  if (millis() - lastStatusSent > 10000) {
    lastStatusSent = millis();
    mqtt.publish((topicBase + "/ESP32/status").c_str(), "online", true);
    
  }
${loopLines}}

void connectWiFi(){
  WiFi.begin(WIFI_SSID,WIFI_PASS);
  while(WiFi.status()!=WL_CONNECTED) delay(300);
}

bool downloadCredentials(){
  HTTPClient http; http.begin(CRED_URL);
  if(http.GET()!=200) return false;
  StaticJsonDocument<1024> d;
  if(deserializeJson(d,http.getString())) return false;
  mqttHost=d["host"].as<String>(); mqttPort=d["port"];
  mqttUser=d["user"].as<String>(); mqttPass=d["pass"].as<String>();
  String caB64=d["caCertBase64"];
  std::vector<uint8_t> cert(2048); size_t len;
  mbedtls_base64_decode(cert.data(),cert.size(),&len,(const unsigned char*)caB64.c_str(),caB64.length());
  cert[len]='\\0'; net.setCACert((const char*)cert.data());
  mqtt.setServer(mqttHost.c_str(),mqttPort); mqtt.setCallback(mqttCallback);
  return true;
}

void connectMQTT(){
  mqtt.connect(clientId.c_str(),mqttUser.c_str(),mqttPass.c_str());
  if(mqtt.connected()) mqtt.subscribe((topicBase+"/+/control").c_str());
}

void mqttCallback(char* topic, byte* payload, unsigned int length){${cbBody}
}

void initTime() {
  configTime(0, 0, "pool.ntp.org", "time.nist.gov");
  Serial.print("Esperando sincronización NTP...");
  time_t now = time(nullptr);
  while (now < 100000) {
    delay(100);
    now = time(nullptr);
  }
  Serial.println("¡Sincronizado!");
}

`;
    setSketch(ino);
  }, [ssid, password, deviceSerial, pinMap, selectedDevice]);

  useEffect(generateSketch, [generateSketch]);

  /* download sketch */
  const downloadSketch = () => {
    const blob = new Blob([sketch], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ESP32_${deviceSerial}.ino`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /* ────────────────────────────── render ────────────────────────────── */
  return (
    <div className={`flex-1 p-6 rounded-lg shadow-xl m-4 ${bgColorClass}  ${textColorClass} transition-colors duration-300`}>
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Código ESP32 – {deviceSerial}</h2>
      </div>

      <div className="grid sm:grid-cols-4 gap-4">
        <label className="text-sm flex flex-col gap-1">
          <span>SSID</span>
          <input
            className="border rounded px-3 py-2"
            value={ssid}
            onChange={(e) => setSsid(e.target.value)}
          />
        </label>
        <label className="text-sm flex flex-col gap-1">
          <span>Contraseña Wi‑Fi</span>
          <input
            type="password"
            className="border rounded px-3 py-2"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
      </div>

      {!selectedDevice?.controls?.length ? (
        <p className="text-sm text-gray-500">No hay controles definidos.</p>
      ) : (
        <table className="w-full text-sm border bg-white dark:bg-gray-900 dark:text-white">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr>
              <th className="border p-2 text-left">Control</th>
              <th className="border p-2 text-left">GPIO</th>
              <th className="border p-2 text-left">Dirección</th>
              <th className="border p-2 text-left">Lectura</th>
            </tr>
          </thead>
          <tbody>
            {selectedDevice.controls.map((ctrl) => (
              <tr key={ctrl.name} className="hover:bg-gray-100 dark:hover:bg-gray-700">
                <td className="border p-2">{ctrl.name}</td>
                <td className="border p-2">
                  <input
                    type="number"
                    className="border rounded w-20 px-2 py-1"
                    value={pinMap[ctrl.name]?.pin || ""}
                    onChange={(e) => setField(ctrl.name, "pin", e.target.value)}
                  />
                </td>
                <td className="border p-2">
                  <select
                    className="border rounded px-2 py-1"
                    value={pinMap[ctrl.name]?.direction || "OUTPUT"}
                    onChange={(e) =>
                      setField(ctrl.name, "direction", e.target.value)
                    }
                  >
                    <option value="OUTPUT">OUTPUT</option>
                    <option value="INPUT">INPUT</option>
                  </select>
                </td>
                <td className="border p-2">
                  <select
                    className="border rounded px-2 py-1"
                    value={pinMap[ctrl.name]?.read || "DIGITAL"}
                    onChange={(e) =>
                      setField(ctrl.name, "read", e.target.value)
                    }
                  >
                    <option value="DIGITAL">DIGITAL</option>
                    <option value="ANALOG">ANALOG</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="flex gap-3">
        <button
          onClick={generateSketch}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Actualizar código
        </button>
        <button
          onClick={downloadSketch}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        >
          Descargar .ino
        </button>
      </div>

      
<div>
  <h3 className="font-medium mb-2 text-sm">Preview</h3>
  <div className="max-h-96 overflow-auto rounded border border-gray-700">
    <SyntaxHighlighter
      language="cpp"
      style={atomDark}
      customStyle={{ fontSize: '0.8rem', padding: '1rem', margin: 0 }}
      showLineNumbers
    >
      {sketch}
    </SyntaxHighlighter>
  </div>
</div>

    </div>
  );
}
